import Bugsnag from "@bugsnag/js";
import {
   addMilliseconds,
   differenceInMilliseconds,
   isPast,
   minutesToMilliseconds,
   parseJSON,
   secondsToMilliseconds
} from "date-fns";
import { first, matches, omit } from "lodash-es";
import find from "lodash-es/find";
import isEmpty from "lodash-es/isEmpty";
import isNil from "lodash-es/isNil";
import isUndefined from "lodash-es/isUndefined";
import { action, autorun, makeAutoObservable, runInAction } from "mobx";

import { apiClient, ensureWretchError } from "../../common/apiClient";
import { API_HOST } from "../../common/environment";
import {
   BackendIssueError,
   GenericError,
   ServerNotFoundError,
   WrongCodeError,
   WrongUsernameOrPasswordError
} from "../../common/errors";
import { logger } from "../../common/logger";
import type { BaseStoreType } from "../../common/types/BaseStoreType";
import type { Company, EnhancedCompany, LoginData, ProfileResponse, Session, User } from "../../common/types/companyTypes";
import {
   type AsyncData,
   initializeWithDefaultData,
   setAsDataAvailable,
   setAsErrorOccured,
   setAsWaitingForData
} from "../../common/utils/asyncDataUtils";
import { formatDateTime } from "../../common/utils/dateUtils";
import {
   getCompanyFromStorage,
   getOrdertypeFromStorage,
   getSessionFromStorage,
   getViewedPromotionArticlesFromStorage,
   setCompanyInStorage,
   setLastCompanyInStorage,
   setSessionInStorage
} from "../../common/utils/storage/storageUtils";
import theme from "../../themes/theme";
import cartStore from "../cart/cartStore";
import categoryStore from "../categories/categoryStore";
import contentStore from "../cms/contentStore";
import segmentStore from "../cms/segmentStore";
import deliveryDatesStore from "../deliveryDates/deliveryDatesStore";
import favoriteStore from "../favoriteStore";
import featuresStore from "../features/featuresStore";
import notificationsStore from "../notifications/notificationsStore";
import productStore from "../product/productStore";
import promotionStore from "../promotionStore";
import subscriptionStore from "../subscriptionStore";
import toastStore from "../toastStore";
import { enhanceCompany } from "./authUtils";
import loginState from "./loginState";

/** Configuration settings for scheduled refresh of auth tokens */
// If there is less than 20 minutes left in the token expiry, we do an immidiate refresh after
const IMMEDIATE_REFRESH_LIMIT = minutesToMilliseconds(20);

// If we are withing the immidiate refresh period, we wait 30 seconds and then trigger refresh
const IMMEDIATE_REFRESH_WAIT = secondsToMilliseconds(30);

// If we are not refreshing right now, we set a timeout and trigger it when there is 20 minutes until expiry
const REFRESH_MARGIN = minutesToMilliseconds(20);

let refeshTimeoutId: ReturnType<typeof setInterval>;
let logoutTimeoutId: ReturnType<typeof setInterval>;

export type LoginRequirement =
   | "CART"
   | "ORDER_CONFIRMATION"
   | "PICKUP_CONFIRMATION"
   | "SEGMENTED_CONTENT"
   | "ACCOUNT_PAGES"
   | "NOT_REQUIRED";

type AuthStoreType = BaseStoreType & {
   user: User | null;
   session: Session | null;
   companies: EnhancedCompany[] | null;
   currentCompany: string | null;
   currentlyRequiresLogin: LoginRequirement[];
   codeChallengeId: string | null;
   resetPasswordId: AsyncData<string | null>;
   resetPasswordProgress: AsyncData<boolean>;
   startStep?: number;
   switchingCompany: boolean;
   username?: string;
   error?: BackendIssueError | null;
   //readonly loggedIn: boolean;

   isLoggedIn(): boolean;
   isPunchoutSession(): boolean;
   getSessionToken(): string | null;
   refreshToken(): Promise<void>;
   scheduleTokenRefresh(session: Session): void;
   abortTokenTimers(): void;
   getSessionFromQuery(): boolean;
   verifySession(session: Session | null): Promise<unknown>;
   verifyStillLoggedIn(visible: boolean): Promise<void>;
   changeCompany(newCompany: string): Promise<void>;
   getCurrentCompany(): EnhancedCompany | undefined;
   getCustomerData(token: string): Promise<LoginData>;
   updatePersonalContactInfo(email: string, firstName: string, lastName: string, mobile: string): Promise<void>;
   updateCompanyContactInfo(email: string, phone: string, phone2: string): Promise<void>;
   getAlgoliaUserToken(): string;
   changePassword(newPassword: string): Promise<string>;
   requestSmsLoginOtp(mobileNumber: string): Promise<void>;
   verifySmsLoginOtp(code: string): Promise<void>;
   requestResetPasswordOtp(email: string): Promise<void>;
   attemptResetPassword(resetCode: string, newPassword: string): Promise<void>;
   clearResetPassword(): void;
   login(username: string, password: string): Promise<void>;
   logout(): void;
};

const authStore: AuthStoreType = makeAutoObservable({
   session: getSessionFromStorage(),
   user: null,
   companies: [],
   currentCompany: null,
   currentlyRequiresLogin: [],
   codeChallengeId: null,
   resetPasswordId: initializeWithDefaultData(null),
   resetPasswordProgress: initializeWithDefaultData(false),
   switchingCompany: false,

   isLoggedIn: () => {
      return loginState.is("LOGGED_IN");
   },

   isPunchoutSession: () => {
      return authStore.user?.loginType === "PUNCHOUT";
   },

   getCustomerData: async (token: string) => {
      const customerDataEndpoint = `${API_HOST}/api/auth/${theme.storeId}/me`;
      const profileData: ProfileResponse = await apiClient(customerDataEndpoint, token).get().json();

      const user: User = omit(profileData, "companies");
      const companies: Company[] = profileData.companies.map((company) => {
         const { businessChainLevel1, businessChainLevel2, businessChainLevel3, businessChainLevel4, ...rest } = company;

         return {
            ...rest,
            businessLevels: {
               level1: businessChainLevel1 || "",
               level2: businessChainLevel2 || "",
               level3: businessChainLevel3 || "",
               level4: businessChainLevel4 || ""
            },
            minimumOrderValue: company.minimumOrderValue || 0
         };
      });
      const currentCompany = first(companies)?.customerNumber;

      if (isNil(currentCompany)) {
         throw new BackendIssueError("Unable to determine default customer number");
      }

      return { user, companies, currentCompany };
   },

   getSessionToken: () => {
      const token = authStore.session?.accessToken;
      if (isUndefined(token)) {
         return null;
      }
      return token;
   },

   refreshToken: async () => {
      logger.log("auth", "Refreshing access token using existing access token");

      if (isNil(authStore.user) || isNil(authStore.session)) {
         logger.warn("auth", "The session is no longer defined in the store, aborting refresh...");
         return Promise.resolve();
      }

      const expiryTime = parseJSON(authStore.session.expiry);
      if (isPast(expiryTime)) {
         logger.warn("auth", `Token past expiry time (${expiryTime}), logout user out...`);
         toastStore.addError(
            "Logget ut pga inaktivitet",
            "Du har blitt logget ut på grunn av inaktivitet. Logg inn igjen for å fortsette handleturen.",
            { context: "login", text: "token_past_expiry_error" }
         );

         loginState.transitionToIfPossible("NOT_LOGGED_IN");
         return Promise.resolve();
      }

      return apiClient(`${API_HOST}/api/refresh`, authStore.getSessionToken())
         .post()
         .json(
            action((newSession) => {
               authStore.scheduleTokenRefresh(newSession);
               authStore.session = newSession;
            })
         )
         .catch((err) => {
            logger.warn("auth", "Unable to refresh access token, will automatically log user out when token expires...", err);
            Bugsnag.notify(err, (e) => {
               e.context = "Token refresh";
            });
         });
   },

   scheduleTokenRefresh: (session) => {
      // Schedule a refresh of the access token so we don't time out
      const expiryTime = parseJSON(session.expiry);
      const expiryIn = differenceInMilliseconds(expiryTime, new Date());

      if (logoutTimeoutId) {
         clearTimeout(logoutTimeoutId);
      }
      logoutTimeoutId = setTimeout(() => loginState.transitionToIfPossible("NOT_LOGGED_IN"), expiryIn);

      // If there is less than 10 minutes until expiry, refresh the token in 30 seconds.
      const refreshIn = expiryIn < IMMEDIATE_REFRESH_LIMIT ? IMMEDIATE_REFRESH_WAIT : expiryIn - REFRESH_MARGIN;
      const refreshAt = addMilliseconds(new Date(), refreshIn);
      logger.log("auth", `Scheduling refresh of access token in ${refreshIn} ms (${formatDateTime(refreshAt)})`);

      refeshTimeoutId = setTimeout(authStore.refreshToken, refreshIn);
   },

   abortTokenTimers: () => {
      if (refeshTimeoutId) {
         clearTimeout(refeshTimeoutId);
      }
      if (logoutTimeoutId) {
         clearTimeout(logoutTimeoutId);
      }
   },

   getSessionFromQuery: () => {
      const params = new URLSearchParams(window.location.search);
      const accessToken = params.get("token");
      const expiry = params.get("expiry");

      if (isNil(accessToken) || isNil(expiry)) {
         return false;
      }

      if (isEmpty(accessToken) || isEmpty(expiry)) {
         return false;
      }

      logger.log("auth", "Creating session data from query string");
      authStore.session = {
         accessToken,
         expiry
      };

      return true;
   },

   verifySession: async (session = null): Promise<void> => {
      const { username } = authStore;
      const usableSession = session === null && authStore.session !== null ? authStore.session : session;

      if (usableSession === null) {
         loginState.transitionTo("NOT_LOGGED_IN");
         return;
      }

      loginState.transitionTo("VERIFY_SESSION");

      let customerResult: LoginData;
      try {
         customerResult = await authStore.getCustomerData(usableSession.accessToken);
      } catch (err) {
         const wErr = ensureWretchError(err);
         if (!isNil(wErr)) {
            logger.log("auth", "Token is likely expired. Response: ", wErr.status);
         }
         loginState.transitionTo("NOT_LOGGED_IN");
         return;
      }

      try {
         logger.log("auth", "Customer Data verified:", customerResult);
         runInAction(() => {
            loginState.transitionTo("LOGGING_IN");
            authStore.session = usableSession;
            authStore.user = customerResult.user;
            authStore.companies = customerResult.companies.map((company) => enhanceCompany(company));
         });

         const { companies, currentCompany } = authStore;
         const usernameCompany = companies?.find((company) => company.customerNumber === username)?.customerNumber;
         const sessionCompany = getCompanyFromStorage();
         const currentCompanyNumber = currentCompany || usernameCompany || sessionCompany;

         if (currentCompanyNumber) {
            await authStore.changeCompany(currentCompanyNumber);
         } else {
            let company: EnhancedCompany | undefined;
            if (companies?.length === 1) {
               logger.log("auth", "COMPANY SELECTION: Only one company available, selecting it automatically");
               company = companies[0];
            } else {
               logger.log("auth", `COMPANY SELECTION: Multiple companies available, checking if username ${username} matches`);
               company = companies?.find((company) => username === company.customerNumber);
            }

            if (company) {
               logger.log("auth", `COMPANY SELECTION: Company found ${company.customerNumber}, proceeding with changeCompany...`);
               await authStore.changeCompany(company.customerNumber);
            } else {
               logger.log("auth", "COMPANY SELECTION: Multiple companies available, showing selection screen");
               loginState.transitionTo("COMPANY_SELECTION");
            }
         }

         authStore.scheduleTokenRefresh(usableSession);
      } catch (err) {
         logger.warn("auth", "Error verifying session", err);
         authStore.logout();
         throw err;
      }
   },

   verifyStillLoggedIn: async (visible) => {
      if (!authStore.isLoggedIn() || !authStore.session || !visible) {
         return;
      }

      try {
         const usableSession = authStore.session;
         await authStore.getCustomerData(usableSession.accessToken);
         logger.log("auth", "Session is verified as still active!");
      } catch (_err) {
         if (cartStore.isEditing()) {
            cartStore.items = [];
            if (authStore.isLoggedIn()) {
               cartStore.saveCartToServerWithoutDelay();
            }
         }
         logger.log("auth", "Session is not longer valid! Logging out...");
         loginState.transitionToIfPossible("FORCED_LOGOUT");
      }
   },

   changeCompany: async (newCompany) => {
      logger.log("auth", `Switching to company ${newCompany}`);
      loginState.transitionTo("COMPANY_SELECTED");

      authStore.error = undefined;
      try {
         if (isNil(authStore.session)) {
            throw new GenericError("MISSING TOKEN");
         }

         authStore.currentCompany = newCompany;
         setCompanyInStorage(newCompany);
         setLastCompanyInStorage(newCompany);

         const company = authStore.getCurrentCompany();
         if (company === undefined) {
            throw new BackendIssueError("Unable to find selected company");
         }

         segmentStore.updateCurrentCustomerProperties();
         loginState.transitionTo("FEATURES_LOADING");

         await featuresStore.fetchEnabledCustomerFeatures(authStore.currentCompany);
         await featuresStore.fetchEnabledConfigCatFeatures(authStore.getCurrentCompany());

         if (featuresStore.navigationTestVariant) {
            company.experiments = {
               navigation: featuresStore.navigationTestVariant
            };
         }

         void favoriteStore.loadFavorites();

         loginState.transitionTo("DELIVERY_DATES_LOADING");

         await deliveryDatesStore.fetchDeliveryDates(authStore.currentCompany);

         const availableOrderTypes = deliveryDatesStore.getAvailableOrderTypes();
         const orderType = getOrdertypeFromStorage(theme.defaultOrderType); // Defaults to order type from SiteConfig

         if (availableOrderTypes === null || availableOrderTypes.length === 0) {
            cartStore.changeOrderType(orderType); // Should be handeled another way as they don't have ordertype
         } else if (availableOrderTypes.includes(orderType)) {
            cartStore.changeOrderType(orderType);
         } else {
            cartStore.changeOrderType(availableOrderTypes[0]);
         }

         const currentDelivery = deliveryDatesStore.getCurrentDelivery();
         if (currentDelivery === null) {
            authStore.error = new BackendIssueError("Missing delivery dates");
            loginState.transitionTo("LOGGED_IN");
            return;
         }

         loginState.transitionTo("ASSORTMENT_LOADING");

         try {
            await productStore.getAssortment(authStore.currentCompany, currentDelivery.date, cartStore.orderType);
            await categoryStore.updateCategorySkus();
         } catch (err) {
            if (err instanceof BackendIssueError) {
               authStore.error = err;
            } else {
               authStore.error = new BackendIssueError("Unable to load assortment, error from backend");
            }

            loginState.transitionTo("LOGGED_IN");
            return;
         }

         loginState.transitionTo("NOTIFICATIONS_LOADING");
         await promotionStore.fetchAvailablePromotions();
         await promotionStore.fetchSignUps();
         void cartStore.loadCartFromServer();
         void subscriptionStore.fetchSubscriptionList();

         if (featuresStore.notificationsEnabled && theme.notifications.enableDeliveryMessages) {
            await notificationsStore.fetchNotifications();
            await notificationsStore.fetchOldNotifications();
         }
         contentStore.viewedPromotionArticleIds = getViewedPromotionArticlesFromStorage(authStore.currentCompany);

         loginState.transitionTo("LOGGED_IN");
      } catch (err) {
         logger.warn("auth", "Error occured during login", err);

         throw err;
      }
   },

   getCurrentCompany: () => {
      return find(authStore.companies, matches({ customerNumber: authStore.currentCompany }));
   },

   getAlgoliaUserToken: () => `company-${authStore.currentCompany}`,

   changePassword: async (newPassword): Promise<string> => {
      const endpoint = `${API_HOST}/api/auth/${theme.storeId}/me/password`;
      try {
         await apiClient(endpoint, authStore.getSessionToken()).content("application/json").post({ newPassword }).text();
         return "";
      } catch (error) {
         logger.log("auth", { error, where: "authStore.changePassword" });
         return error && typeof error === "object" && "text" in error ? (error.text as string) : "unknown error cause";
      }
   },

   updateCompanyContactInfo: async (email, phone1, phone2) => {
      if (!authStore.isLoggedIn() || isNil(authStore.user)) {
         toastStore.addError("Det oppsto en feil", "Prøv å logge ut og inn igjen for å oppdatere profilen.", {
            context: "login",
            text: "not_logged_in_error"
         });
         return;
      }
      const currentCompany = authStore.currentCompany;
      if (isNil(currentCompany)) {
         return;
      }

      const res: Company = await apiClient(
         `${API_HOST}/api/auth/${theme.storeId}/company/${currentCompany}`,
         authStore.getSessionToken()
      )
         .post({
            email,
            phone1,
            phone2
         })
         .json();
      runInAction(() => {
         const infoIsUpdated: boolean = res.email === email && res.phone1 === phone1 && res.phone2 === phone2;
         if (!infoIsUpdated) {
            throw "Unable to save new contact info";
         }

         logger.log("auth", "Updated email, phone and phone2 successfully");

         const companyIndex = authStore.companies?.findIndex((company) => company.customerNumber === currentCompany);
         if (!isNil(authStore.companies) && !isNil(companyIndex)) {
            authStore.companies[companyIndex].email = email;
            authStore.companies[companyIndex].phone1 = phone1;
            authStore.companies[companyIndex].phone2 = phone2;
         }
      });
   },

   updatePersonalContactInfo: async (email, firstName, lastName, mobile) => {
      if (!authStore.isLoggedIn() || isNil(authStore.user)) {
         toastStore.addError("Det oppsto en feil", "Prøv å logge ut og inn igjen for å oppdatere profilen.", {
            context: "login",
            text: "not_logged_in_error "
         });
         return;
      }

      const currentCompany = authStore.currentCompany;
      if (isNil(currentCompany)) {
         return;
      }

      const res: User = await apiClient(`${API_HOST}/api/auth/${theme.storeId}/me`, authStore.getSessionToken())
         .post({
            id: authStore.user.id,
            email,
            firstName,
            lastName,
            mobile
         })
         .json();

      logger.log("auth", "Updated firstName, lastName, email and mobile successfully");

      authStore.user.email = res.email;
      authStore.user.firstName = res.firstName;
      authStore.user.lastName = res.lastName;
      authStore.user.mobile = res.mobile;
   },

   requestSmsLoginOtp: async (mobileNumber) => {
      authStore.codeChallengeId = await apiClient(`${API_HOST}/api/auth/${theme.storeId}/sms/requestcode`)
         .query({
            mobileNumber
         })
         .post()
         .text();
   },

   verifySmsLoginOtp: async (code) => {
      loginState.transitionTo("CHECKING_LOGIN");

      try {
         const session: Session = await apiClient(`${API_HOST}/api/auth/${theme.storeId}/sms/verify`)
            .query({
               challengeId: authStore.codeChallengeId,
               code
            })
            .post()
            .json();

         logger.log("auth", "Verifying OneTimeCode session", session);
         await authStore.verifySession(session);

         // Clear the codeChallengeId once the progress dialog has disappeared
         setTimeout(() => {
            authStore.codeChallengeId = null;
         }, 500);
      } catch (err) {
         loginState.transitionTo("LOGIN_MODAL");

         const wErr = ensureWretchError(err);
         if (wErr?.response?.status === 403) {
            throw new WrongCodeError();
         }
         logger.log("auth", "Got error during OneTimeCode login: ", err);
         throw new BackendIssueError("Unable to verify OneTimeCode");
      }
   },

   requestResetPasswordOtp: async (email: string) => {
      const transitionedSuccessfully = setAsWaitingForData(authStore.resetPasswordId);

      if (!transitionedSuccessfully) {
         return Promise.reject();
      }

      return apiClient(`${API_HOST}/api/auth/${theme.storeId}/forgottenpassword`)
         .query({
            email
         })
         .post()
         .text((resetId) => {
            setAsDataAvailable(authStore.resetPasswordId, resetId);
         })
         .catch((errorObject) => {
            setAsErrorOccured(authStore.resetPasswordId, `${errorObject}`);
         });
   },

   attemptResetPassword: async (code: string, newPassword: string) => {
      const transitionedSuccessfully = setAsWaitingForData(authStore.resetPasswordProgress);

      if (!transitionedSuccessfully) {
         return Promise.reject();
      }

      return apiClient(`${API_HOST}/api/auth/${theme.storeId}/resetpassword`)
         .query({ resetId: authStore.resetPasswordId.data, code, newPassword })
         .post()
         .res((res) => {
            setAsDataAvailable(authStore.resetPasswordProgress, res.ok);
         })
         .catch((errorObject) => {
            setAsErrorOccured(authStore.resetPasswordProgress, `${errorObject}`);
         });
   },

   clearResetPassword: () => {
      authStore.resetPasswordId = initializeWithDefaultData(null);
      authStore.resetPasswordProgress = initializeWithDefaultData(false);
   },

   login: async (username, password) => {
      loginState.transitionTo("CHECKING_LOGIN");
      logger.log("auth", `Starting login process using host ${API_HOST}`);
      // Need to set it to a initial value, because typescript will not understand that the catch-block guarantees a
      // value being set before next try/catch block
      let session: Session = { expiry: "", accessToken: "" };

      try {
         // Attempting to get session with access token for user
         session = await apiClient(`${API_HOST}/api/auth/${theme.storeId}/login`).query({ username, password }).post().json();
      } catch (err: unknown) {
         loginState.transitionTo("LOGIN_MODAL");
         const wErr = ensureWretchError(err);
         if (wErr !== null) {
            switch (wErr.response.status) {
               case 400:
               case 401:
                  if (wErr.text === "StoreId not found") {
                     throw new BackendIssueError("StoreId not found");
                  }
                  throw new WrongUsernameOrPasswordError();

               case 500:
                  throw new BackendIssueError();
            }
         }
         throw new ServerNotFoundError();
      }

      try {
         logger.log("auth", "Verifying session", session);
         runInAction(() => {
            authStore.username = username;
         });

         await authStore.verifySession(session);
      } catch (err: unknown) {
         logger.log("auth", "Got error during login: ", err);
         loginState.transitionTo("NOT_LOGGED_IN");

         if (err instanceof Error) {
            Bugsnag.notify(err, (e) => {
               e.context = "Session Verification";
            });
         }
         if (err instanceof BackendIssueError) {
            throw err;
         }
         throw new BackendIssueError();
      }
   },

   logout: action(() => {
      authStore.abortTokenTimers();
      authStore.user = null;
      authStore.session = null;
      authStore.companies = [];
      authStore.currentCompany = null;
      setCompanyInStorage(null);
   }),

   clearCompanySpecificData: () => {}
} satisfies AuthStoreType);

// Save user info to session storage whenever it changes
autorun(() => setSessionInStorage(authStore.session));

// Make customer info available for Google Analytics and other software outside React
autorun(() => {
   window.isUserLoggedIn = authStore.isLoggedIn();
   window.companyInfo = authStore.getCurrentCompany();
   window.customerInfo = authStore.user;
});

export default authStore;
